import React from "react";
import {
    Box, Text, Stack, Button, Card, Badge, Inline
} from "braid-design-system";
import { PromoHedResponse, getAllCustomerGroups } from "../../services/promotionService";

interface PromotionsViewModalProps {
    isOpen: boolean;
    promotions: PromoHedResponse[];
    selectedPromotionId?: number | null;
    onSelect?: (promo: PromoHedResponse) => void;
    onClose: () => void;
}

interface AppliedPromotion {
    type: string;
    label: string;
    value: string;
}

const getAppliedPromotions = (promo: PromoHedResponse): AppliedPromotion[] => {
    const result: AppliedPromotion[] = [];

    if (promo.promo_disper != null && promo.promo_disper > 0) {
        result.push({ type: 'discount_percent', label: 'Discount %', value: `${promo.promo_disper}%` });
    }
    if (promo.promo_disamt != null && promo.promo_disamt > 0) {
        result.push({ type: 'discount_value', label: 'Discount Value', value: `${promo.promo_disamt.toFixed(2)}` });
    }
    if (promo.promo_free_item) {
        result.push({ type: 'free_item', label: 'Free Item', value: promo.promo_free_item });
    }
    if (promo.promo_free_voucher) {
        result.push({ type: 'free_voucher', label: 'Free Voucher', value: promo.promo_free_voucher });
    }

    return result;
};

const getCriteriaLabel = (type: string): string => {
    const map: Record<string, string> = {
      item_group: 'Group',
      item: 'Item',
      amount_range: 'Amount',
      payment_mode: 'Paymode',
      location: 'Location',
      customer_group: 'Customer Group',
    };
    return map[type] || type;
};

const formatCriteriaValues = (
    criteria: {
        criteria_type: string;
        criteria_value?: string | null;
        amount_min?: number | null;
        amount_max?: number | null;
    },
    customerGroupMap: Record<string, string> = {}
): string => {
    if (criteria.criteria_type === 'amount_range') {
        if (criteria.amount_min == null && criteria.amount_max == null) return 'None';
        return `${criteria.amount_min ?? 0} - ${criteria.amount_max ?? '∞'}`;
    }
    const raw = criteria.criteria_value || '';
    if (!raw.trim()) return 'None';
    if (criteria.criteria_type === 'customer_group') {
        return raw
            .split(',')
            .map(v => customerGroupMap[v.trim()] || v.trim())
            .join(', ');
    }
    return raw.split(',').map(v => v.trim()).join(', ');
};

const PromotionsViewModal: React.FC<PromotionsViewModalProps> = ({
    isOpen,
    promotions,
    selectedPromotionId,
    onSelect,
    onClose,
}) => {
    const [customerGroupMap, setCustomerGroupMap] = React.useState<Record<string, string>>({});
    const activePromotions = promotions.filter(promo => promo.promo_active);

    React.useEffect(() => {
        if (!isOpen) return;
        getAllCustomerGroups()
            .then((groups) => {
                const map: Record<string, string> = {};
                groups.forEach((g) => {
                    if (g.id != null && g.group_name) {
                        map[String(g.id)] = g.group_name;
                    }
                });
                setCustomerGroupMap(map);
            })
            .catch(() => {});
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="search-modal-overlay">
            <div className="search-modal">
                <div className="search-modal-header">
                    <Stack space="xsmall">
                        <Text weight="strong" size="large">
                            Active Promotions
                        </Text>
                        <Text tone="secondary" size="small">
                            {activePromotions.length} active promotion{activePromotions.length !== 1 ? 's' : ''} available
                        </Text>
                    </Stack>
                    <button type="button" onClick={onClose} className="search-modal-close">
                        ✕
                    </button>
                </div>

                <div className="search-modal-content">
                    {activePromotions.length === 0 ? (
                        <Box paddingY="large" textAlign="center">
                            <Text tone="secondary">
                                No active promotions available at the moment.
                            </Text>
                        </Box>
                    ) : (
                        <Stack space="medium">
                            {activePromotions.map((promo) => {
                                const appliedTypes = getAppliedPromotions(promo);
                                return (
                                <div key={promo.id} 
                                    onClick={() => onSelect?.(promo)}
                                    className={selectedPromotionId === promo.id ? 'promo-card-selected' : 'promo-card-clickable'}
                                >
                                <Card>
                                        <Box padding="medium">
                                            <Stack space="small">
                                                <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                                                    <Stack space="xsmall">
                                                        <Text weight="strong" size="standard">
                                                            {promo.promo_name}
                                                        </Text>
                                                        <Inline space="small" alignY="center">
                                                            <Text size="small" weight="strong">
                                                                Code:
                                                            </Text>
                                                            <Badge tone="info">
                                                                {promo.promo_code}
                                                            </Badge>
                                                        </Inline>
                                                        <Inline space="small" alignY="center">
                                                            <Text size="small" weight="strong">
                                                                Type:
                                                            </Text>
                                                            <Badge tone="promote">
                                                                {promo.promo_type_code || 'N/A'}
                                                            </Badge>
                                                        </Inline>
                                                    </Stack>
                                                    <Box textAlign="right">
                                                        <Box display="flex" flexDirection="column" alignItems="flexEnd" gap="xsmall">
                                                            <Badge tone="positive">
                                                                Active
                                                            </Badge>
                                                            {promo.promo_datefrom && promo.promo_dateto ? (
                                                                <Text size="small" tone="secondary">
                                                                    {promo.promo_datefrom} ~ {promo.promo_dateto}
                                                                </Text>
                                                            ) : null}
                                                            {(promo.promo_timefrom || promo.promo_timeto) ? (
                                                                <Text size="xsmall" tone="secondary">
                                                                    {promo.promo_timefrom || '00:00'} ~ {promo.promo_timeto || '23:59'}
                                                                </Text>
                                                            ) : null}
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {appliedTypes.length > 0 ? (
                                                    <Box>
                                                        <Inline space="small" alignY="center">
                                                            {appliedTypes.map((applied) => (
                                                                <Inline key={applied.type} space="xsmall" alignY="center">
                                                                    <Text weight="strong" size="small">
                                                                        {applied.label}:
                                                                    </Text>
                                                                    <Badge tone="promote">
                                                                        {applied.value}
                                                                    </Badge>
                                                                </Inline>
                                                            ))}
                                                        </Inline>
                                                    </Box>
                                                ) : null}

                                                {promo.details && promo.details.length > 0 ? (
                                                    <Box marginTop="xsmall">
                                                        <Text weight="strong" size="small">
                                                            Offer Criteria
                                                        </Text>
                                                        <Box marginTop="xsmall">
                                                            <Stack space="xsmall">
                                                                {promo.details.map((detail, idx) => (
                                                                    <Text key={idx} size="small" tone="secondary">
                                                                        {getCriteriaLabel(detail.criteria_type)} | {formatCriteriaValues(detail, customerGroupMap)}
                                                                    </Text>
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Text tone="secondary" size="small">
                                                        No specific criteria defined (applies to all items)
                                                    </Text>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Card>
                                </div>
                                );
                            })}
                        </Stack>
                    )}
                </div>

                <Box padding="medium" display="flex" justifyContent="flexEnd">
                    <Button variant="solid" tone="brandAccent" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </div>
        </div>
    );
};

export default PromotionsViewModal;